import {
  NodeId,
  NodeModel,
  SwimlaneNodeModel,
} from '@diagram/model/node.model';
import { GetState, SetState } from '../store.types';
import { Connection, Position, XYPosition } from '@xyflow/react';
import { EdgeId, EdgeModel } from '../../model/edge.model';
import { DrillDownId, DrillDownModel } from '../../model/drilldown.model';
import { DiagramModel } from '../../model/diagram.model';
import { Orientation, SwimlaneGroupNodeModel } from '../../model/node.model';
import { edgeFactory } from '../../model/edge.factory';

import { v4 as uuidv4 } from 'uuid';
import { makeNode } from '../../model/node.factory';
import { calculateSwimlaneRect, shiftSwimlanes } from '../../utils/geometry';
import { ChangeModel } from '@diagram/model/change.model';
import { useDragMonitor } from '../../../../store/dragMonitorStore';
import shiftNodesForSpacing from '../../../../utils/shiftNodesForSpacing';
import { updateDiagramDataInRTDB } from '@diagram/service/diagram.rtdb';

export interface HandlerSlice {
  updateNodeLabel(nodeId: NodeId, label: string): void;
  getUsedHandles(nodeId: NodeId): Record<Position, string> | null;
  selectNode(nodeId: NodeId): void;
  getSelectedElements(): { nodes: NodeModel[]; edges: EdgeModel[] };
  unSelectAll(): void;

  drilldown: {
    setActiveDrilldown(id: DrillDownId): void;
    patchDrilldown(data: Partial<DrillDownModel>): void;
    deleteDrilldown(drilldownId: DrillDownId): void;
    setActiveCategory(categoryId: string): void;
    highlightElements(drillDown: DrillDownModel, highlight: boolean): void;
  };

  edge: {
    createEdge(connection: Connection): void;
    updateEdgeLabel(edgeId: EdgeId, label: string): void;
    onConnect(connection: Connection): void;
    deleteEdge(edgeId: EdgeId): void;
    onReconnect(edge: EdgeModel, connection: Connection): void;
  };

  node: {
    getConnectedEdges(nodeId: NodeId): EdgeModel[];
    getIncomers(nodeId: NodeId): NodeId[];
    getOutgoers(nodeId: NodeId): NodeId[];
    onNodeSelect(nodeId: NodeId): void;
    insertNodeOnEdge(edgeId: string, node: NodeModel): void;
    onNodesDelete(nodes: NodeModel[]): void;
    onNodeDragStart: (_: React.MouseEvent, node: NodeModel) => void;
    onNodeDragStop: (_: React.MouseEvent, node: NodeModel) => void;
  };
  // moving nodes
  swimlane: {
    createSwimlaneGroup(orientation: Orientation, position: XYPosition): void;
    insertLaneAt(nodeId: NodeId, index: number): void;
    deleteLane(laneId: NodeId): void;
    getLaneOrder(nodeId: NodeId): NodeId[] | null;
    getSortedLanes(groupId: NodeId): NodeModel[] | null;
    getFullSwimlaneGroup(nodeId: NodeId): {
      sortedLanes: SwimlaneNodeModel[];
      group: SwimlaneGroupNodeModel;
    } | null;
  };

  meta: {
    receiveUpdate(data: Partial<DiagramModel>): void;
    updateData(data: Partial<DiagramModel>): void;
    updateSettings(settings: Partial<DiagramModel['settings']>): void;
    toggleSnapToGrid(): void;
  };
  // addSwimlane
}

export const createHandlerSlice = (
  set: SetState,
  get: GetState
): HandlerSlice => ({
  getSelectedElements: () => {
    const nodes = get().rfNodes.filter((n) => n.selected);
    const edges = get().rfEdges.filter((e) => e.selected);
    return { nodes, edges };
  },
  unSelectAll: () => {
    const { nodes, edges } = get().getSelectedElements();
    const nodePatches = nodes.map((n) => ({
      id: n.id,
      patch: { selected: false } as Partial<NodeModel>,
    }));
    get().patchNodes(nodePatches);
    const edgePatches = edges.map((e) => ({
      id: e.id,
      patch: { selected: false } as Partial<EdgeModel>,
    }));
    get().patchEdges(edgePatches);
  },
  updateNodeLabel: (nodeId, label) => {
    const node = get().nodesById[nodeId];
    if (!node) return;
    const before = node.data.label;
    if (before === label) return;

    get().patchNodes([
      {
        id: nodeId,
        patch: {
          data: { ...node.data, label },
        } as Partial<NodeModel>,
      },
    ]);

    get().recordOperation([
      {
        type: 'update:node',
        status: 'end',
        id: nodeId,
        data: {
          before: { data: node.data } as Partial<NodeModel>,
          after: { data: { ...node.data, label } } as Partial<NodeModel>,
        },
      },
    ]);
  },
  getUsedHandles: (nodeId) => {
    const edges = get().rfEdges;

    const usedHandles = Object.entries(edges).reduce((acc, [edgeId, edge]) => {
      if (edge.source === nodeId) {
        const sourcePosition = edge.data?.sourcePosition as string;
        acc[sourcePosition as Position] = edgeId;
      }

      if (edge.target === nodeId) {
        const targetPosition = edge.data?.targetPosition as string;
        acc[targetPosition as Position] = edgeId;
      }
      return acc;
    }, {} as Record<Position, string>);

    if (!Object.keys(usedHandles).length) {
      return null;
    }
    return usedHandles;
  },
  selectNode: (nodeId) => {
    const node = get().nodesById[nodeId];
    if (!node) return; // node not found, don't do anything

    const nodes = get().rfNodes;
    const unselectPatches = nodes.reduce((acc, n) => {
      {
        if (n.id !== nodeId && n.selected) {
          acc.push({
            id: n.id,
            patch: { selected: false } as Partial<NodeModel>,
          });
        }
        return acc;
      }
    }, [] as { id: NodeId; patch: Partial<NodeModel> }[]);

    const patches = [
      ...unselectPatches,
      { id: nodeId, patch: { selected: !node.selected } as Partial<NodeModel> },
    ];

    get().patchNodes(patches as { id: NodeId; patch: Partial<NodeModel> }[]);
  },
  drilldown: {
    setActiveDrilldown: (id) => {
      const activeDrillDownId = get().activeDrilldown;
      const drillDown = get().drilldowns[id];
      if (activeDrillDownId === id) {
        get().drilldown.highlightElements(drillDown, false);
        set(() => ({
          activeDrilldown: '' as DrillDownId,
        }));
        return;
      }
      set(() => ({
        activeDrilldown: id,
      }));
      get().drilldown.highlightElements(drillDown, true);
    },
    patchDrilldown: (data) => {
      const id = get().activeDrilldown;
      let drilldown = {} as DrillDownModel;
      if (!id) {
        drilldown = {
          ...data,
          id: uuidv4() as DrillDownId,
        } as DrillDownModel;
      } else {
        drilldown = get().drilldowns[id];
      }

      set((s) => ({
        drilldowns: {
          ...s.drilldowns,
          [drilldown.id]: drilldown,
        },
      }));

      get().recordOperation([
        {
          type: id ? 'update:drilldown' : 'create:drilldown',
          status: 'end',
          id: drilldown.id,
          ...(id
            ? { data: { before: drilldown, after: { ...drilldown, ...data } } }
            : { data: drilldown }),
        } as ChangeModel,
      ]);
    },
    deleteDrilldown: (id) => {
      const drilldowns = get().drilldowns;
      if (!drilldowns[id]) return;
      get().recordOperation([
        {
          type: 'delete:drilldown',
          status: 'end',
          id,
          data: drilldowns[id],
        } as ChangeModel,
      ]);
      delete drilldowns[id];

      set(() => ({
        drilldowns: { ...drilldowns },
      }));
    },
    setActiveCategory: (categoryId: string) => {
      get().unSelectAll();
      if (!categoryId) {
        set(() => ({
          activeCategory: '',
        }));
        return;
      }

      const drilldowns = get().drilldowns;

      const activeDrilldown = get().activeDrilldown;
      if (
        activeDrilldown &&
        drilldowns[activeDrilldown].categoryId === categoryId
      ) {
        get().drilldown.setActiveDrilldown('' as DrillDownId);
      }

      const filteredDrillDowns = Object.values(drilldowns).filter(
        (dd) => dd.categoryId === categoryId
      );

      filteredDrillDowns.forEach((drillDown) => {
        get().drilldown.highlightElements(drillDown, true);
      });

      set(() => ({
        activeCategory: categoryId,
      }));
    },
    highlightElements: (drillDown, highlight) => {
      const nodesById = get().nodesById;
      const edgesById = get().edgesById;
      const nodePatches = drillDown.nodes.map((nodeId) => {
        return {
          id: nodeId,
          patch: {
            selected: highlight,
            data: {
              ...nodesById[nodeId].data,
              color: highlight ? drillDown.color : '#000000',
            },
          },
        };
      }) as { id: NodeId; patch: Partial<NodeModel> }[];
      const edgePatches = drillDown.edges?.map((edgeId) => {
        const edge = edgesById[edgeId];
        if (!edge) return null;
        return {
          id: edgeId,
          patch: {
            selected: highlight,
            data: {
              ...edge.data,
              color: highlight ? drillDown.color : '#000000',
            },
          },
        };
      }) as { id: EdgeId; patch: Partial<EdgeModel> }[];

      get().patchNodes(nodePatches || []);
      get().patchEdges(edgePatches || []);
    },
  },
  edge: {
    updateEdgeLabel: (id, label) => {
      const edge = get().edgesById[id];
      if (!edge) return;
      const before = edge.data?.label || '';
      if (before === label) return;

      get().patchEdges([
        {
          id,
          patch: {
            data: { ...edge.data, label },
          } as Partial<EdgeModel>,
        },
      ]);

      get().recordOperation([
        {
          type: 'update:edge',
          status: 'end',
          id,
          data: { before, after: label },
        } as ChangeModel,
      ]);
    },
    createEdge: (connection) => {
      const { snapToGrid } = get().data.settings;
      const newEdge = edgeFactory(snapToGrid, connection) as EdgeModel;
      get().addEdges([newEdge]);
      get().recordOperation([
        {
          type: 'create:edge',
          status: 'end',
          id: newEdge.id,
          data: newEdge,
        },
      ]);
    },
    onConnect: (connection) => {
      const { snapToGrid } = get().data.settings;

      const newEdge = edgeFactory(snapToGrid, connection) as EdgeModel;

      get().addEdges([newEdge]);
      get().recordOperation([
        {
          type: 'create:edge',
          status: 'end',
          id: newEdge.id,
          data: newEdge,
        },
      ]);
    },
    deleteEdge: (edgeId) => {
      const edge = get().edgesById[edgeId];
      get().patchEdges([{ id: edgeId, patch: null }]);
      get().recordOperation([
        {
          type: 'delete:edge',
          status: 'end',
          id: edgeId,
          data: edge,
        } as ChangeModel,
      ]);
    },
    onReconnect: (edge, connection) => {
      const { snapToGrid } = get().data.settings;
      const newEdge = edgeFactory(
        snapToGrid,
        connection,
        edge.data?.label
      ) as EdgeModel;

      get().patchEdges([
        { id: edge.id, patch: null },
        { id: newEdge.id, patch: newEdge },
      ]);
      get().recordOperation([
        {
          type: 'delete:edge',
          status: 'end',
          id: edge.id,
          data: edge,
        },
        {
          type: 'create:edge',
          status: 'end',
          id: newEdge.id,
          data: newEdge,
        } as ChangeModel,
      ]);
    },
  },
  node: {
    getIncomers: (nodeId) => {
      const incomers: NodeId[] = [];
      const edges = get().rfEdges;
      edges.forEach((edge) => {
        if (edge.target === nodeId) {
          incomers.push(edge.source as NodeId);
        }
      });
      return incomers;
    },
    getOutgoers: (nodeId) => {
      const outgoers: NodeId[] = [];
      const edges = get().rfEdges;
      edges.forEach((edge) => {
        if (edge.source === nodeId) {
          outgoers.push(edge.target as NodeId);
        }
      });
      return outgoers;
    },
    getConnectedEdges: (id) => {
      const connectedEdges: EdgeModel[] = [];
      const edges = get().rfEdges;
      edges.forEach((edge) => {
        if (edge.source === id || edge.target === id) {
          connectedEdges.push(edge as EdgeModel);
        }
      });
      return connectedEdges;
    },
    insertNodeOnEdge: (edgeId, node) => {
      const snapToGrid = get().data.settings.snapToGrid;
      const autoSpacing = get().data.settings.autoSpacing;
      const edge = get().edgesById[edgeId];

      const { setHoveredEdge, setDraggingNode } = useDragMonitor.getState();

      if (!edge.sourceHandle || !edge.targetHandle) return;

      const newEdges = [
        ...[
          edgeFactory(snapToGrid, {
            source: edge.source,
            target: node.id,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
          }) as EdgeModel,
        ],
        ...[
          edgeFactory(snapToGrid, {
            source: node.id,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
          }) as EdgeModel,
        ],
      ];

      const nodesById = get().nodesById;

      let newNodes = [] as NodeModel[] | null;

      nodesById[node.id] = node;

      if (autoSpacing) {
        newNodes = shiftNodesForSpacing(nodesById, node.id);
      } else {
        get().addNodes([node]);
      }

      if (newNodes === null) {
        return;
      }
      if (autoSpacing) {
        get().patchNodes(newNodes.map((n) => ({ id: n.id, patch: n })));
        get().recordOperation(
          newNodes.map(
            (n) =>
              ({
                type: 'update:node',
                status: 'end',
                id: n.id as NodeId,
                data: {
                  before: nodesById[n.id] ? nodesById[n.id] : null,
                  after: n,
                },
              } as ChangeModel)
          )
        );
      }
      get().patchEdges([
        { id: edgeId as EdgeId, patch: null },
        ...newEdges.map((e) => ({ id: e.id, patch: e })),
      ]);
      setHoveredEdge(null);
      setDraggingNode(null, null);
      // record insertion as one operation
      get().recordOperation([
        {
          type: 'create:node',
          status: 'end',
          id: node.id,
          data: node,
        },
        {
          type: 'delete:edge',
          status: 'end',
          id: edgeId as EdgeId,
          data: edge,
        },
        ...newEdges.map(
          (e) =>
            ({
              type: 'create:edge',
              status: 'end',
              id: e.id as EdgeId,
              data: e,
            } as ChangeModel)
        ),
      ]);
    },
    onNodesDelete: (deleted) => {
      const nodes = get().rfNodes;
      let remainingNodes = [...nodes];
      const changes: ChangeModel[] = [];

      get().setEdges(
        deleted.reduce((acc, node) => {
          if (node.type === 'swimlane') {
            get().swimlane.deleteLane(node.id);
            return acc;
          }

          const outgoers = get().node.getOutgoers(node.id);
          const incomers = get().node.getIncomers(node.id);
          const connectedEdges = get().node.getConnectedEdges(node.id);
          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge)
          );

          const createdEdges = incomers.flatMap((source) =>
            outgoers.map(
              (target) =>
                ({
                  id: `${source}->${target}` as EdgeId,
                  source,
                  target,
                  sourceHandle: connectedEdges.find(
                    (e) => e.source === source && e.target === node.id
                  )?.sourceHandle,
                  targetHandle: connectedEdges.find(
                    (e) => e.source === node.id && e.target === target
                  )?.targetHandle,
                  data: {
                    label: '',
                  },
                  type: 'insertSmooth',
                } as EdgeModel)
            )
          );
          changes.push({
            type: 'delete:node',
            status: 'end',
            id: node.id,
            data: node,
          });
          changes.push(
            ...connectedEdges.map(
              (e) =>
                ({
                  type: 'delete:edge',
                  status: 'end',
                  id: e.id as EdgeId,
                  data: e,
                } as ChangeModel)
            )
          );
          changes.push(
            ...createdEdges.map(
              (e) =>
                ({
                  type: 'create:edge',
                  status: 'end',
                  id: e.id as EdgeId,
                  data: e,
                } as ChangeModel)
            )
          );
          remainingNodes = remainingNodes.filter((rn) => rn.id !== node.id);
          return [...remainingEdges, ...createdEdges] as EdgeModel[];
        }, [] as EdgeModel[])
      );

      get().setNodes(remainingNodes);

      get().recordOperation([...changes]);
    },
    onNodeDragStart: () => console.log(),
    onNodeDragStop: () => console.log(),
    onNodeSelect: (nodeId) => {
      const node = get().nodesById[nodeId];
      if (!node) return; // node not found, don't do anything
      // if other nodes are selected, see if any edges are in between
      // if so, select those edges as well
      const nodes = get().rfNodes.filter((n) => n.selected);
      const edges = get().rfEdges.filter((e) => e.selected);

      const connectedEdges = get().rfEdges.filter((e) => {
        return (
          nodes.some((n) => n.id === e.source || nodeId === e.source) &&
          nodes.some((n) => n.id === e.target || nodeId === e.target)
        );
      });

      const edgePatches = connectedEdges
        .filter((e) => !edges.includes(e))
        .map((e) => ({
          id: e.id,
          patch: { selected: true } as Partial<EdgeModel>,
        }));

      get().patchEdges(edgePatches);
    },
  },
  swimlane: {
    createSwimlaneGroup: (orientation, position) => {
      const nodesToAdd: NodeModel[] = [];

      const { width, height } =
        orientation === 'horizontal'
          ? { width: 1000, height: 600 }
          : { width: 600, height: 1000 };

      const swimlaneGroupId = uuidv4() as NodeId;

      const laneCount = 4;

      for (let i = 0; i < laneCount; i++) {
        const lane = makeNode({
          id: uuidv4() as NodeId,
          type: 'swimlane',
          parentId: swimlaneGroupId,
          data: {
            orientation,
            label: `Lane ${i + 1}`,
            index: i,
            color: '#000000',
          },
          ...calculateSwimlaneRect(orientation, height, width, i),
        });

        nodesToAdd.push(lane);
      }

      const swimlaneGroup = makeNode({
        id: swimlaneGroupId,
        type: 'swimlaneGroup',
        data: {
          orientation,
          label: 'Swimlane Group',
          laneOrder: nodesToAdd.map((n) => n.id),
          index: 0,
          color: '#000000',
        },
        x: position.x,
        y: position.y,
        width,
        height,
      });

      nodesToAdd.unshift(swimlaneGroup);

      get().addNodes(nodesToAdd);
      get().recordOperation([
        {
          type: 'create:swimlaneGroup',
          status: 'end',
          id: swimlaneGroupId,
          data: nodesToAdd,
        },
      ]);
    },
    getLaneOrder: (nodeId) => {
      const node = get().nodesById[nodeId];

      if (node?.type !== 'swimlaneGroup' || !node || !node.data.laneOrder)
        return null;
      return node.data.laneOrder;
    },
    getSortedLanes: (groupId) => {
      const groupNode = get().nodesById[groupId];
      if (!groupNode || groupNode.type !== 'swimlaneGroup') return null;
      const nodes = get().rfNodes;

      const laneNodes = nodes.filter((n) => n.parentId === groupId);

      const sortedLanes = laneNodes.sort((a, b) => {
        const order = groupNode.data.laneOrder;
        if (!order) return 0;
        return order.indexOf(a.id) - order.indexOf(b.id);
      });
      // return groupNode followed by sorted lanes,
      return [...sortedLanes];
    },
    getFullSwimlaneGroup: (nodeId) => {
      const node = get().nodesById[nodeId];
      const parentId = node?.parentId;
      if (!node || !parentId) return null;
      const group = get().nodesById[parentId] as SwimlaneGroupNodeModel;
      if (!node || node.type !== 'swimlane' || !parentId) return null;
      const sortedLanes = get().swimlane.getSortedLanes(
        node.parentId!
      ) as SwimlaneNodeModel[];
      if (!sortedLanes) return null;

      return { sortedLanes, group };
    },
    insertLaneAt: (laneId, index) => {
      const { group, sortedLanes } =
        get().swimlane.getFullSwimlaneGroup(laneId) || {};
      if (!group || !sortedLanes) return;

      const currentIndex = sortedLanes.findIndex((lane) => lane.id === laneId);

      const newIndex = currentIndex + index;

      const lanesToShift = sortedLanes.slice(newIndex);
      const currentLane = sortedLanes[currentIndex];

      const horizontal = group.data.orientation === 'horizontal';

      const width = horizontal ? group.width : currentLane.width;
      const height = horizontal ? currentLane.height : group.height;
      const deltaX = horizontal ? 0 : width;
      const deltaY = horizontal ? height : 0;

      const { x, y } =
        newIndex === 0
          ? { x: 0, y: 0 }
          : newIndex === currentIndex
          ? {
              x: currentLane.position.x,
              y: currentLane.position.y,
            }
          : {
              x: currentLane.position.x + (horizontal ? 0 : currentLane.width),
              y: currentLane.position.y + (horizontal ? currentLane.height : 0),
            };

      const shiftedLanes = shiftSwimlanes(lanesToShift, group, deltaX, deltaY);

      const newLaneId = uuidv4() as NodeId;

      const newLane = makeNode({
        id: newLaneId,
        type: 'swimlane',
        parentId: group.id,
        data: {
          orientation: group.data.orientation as Orientation,
          label: `Lane ${sortedLanes.length + 1}`,
          index: sortedLanes.length + 20,
          color: '#000000',
        },
        x,
        y,
        width,
        height,
      });

      get().patchNodes([
        ...shiftedLanes,
        { id: newLane.id, patch: newLane as Partial<NodeModel> },
        {
          id: group.id,
          patch: {
            data: {
              ...group.data,
              laneOrder: [
                ...group.data.laneOrder.slice(0, newIndex),
                newLaneId,
                ...group.data.laneOrder.slice(newIndex),
              ],
            },
          } as Partial<NodeModel>,
        },
      ]);

      get().recordOperation([
        {
          type: 'update:swimlaneGroup',
          status: 'end',
          id: group.id,
          data: {
            before: [group, ...sortedLanes],
            after: [
              {
                ...group,
                data: {
                  ...group.data,
                  laneOrder: [
                    ...group.data.laneOrder.slice(0, newIndex),
                    newLaneId,
                    ...group.data.laneOrder.slice(newIndex),
                  ],
                },
              },
              ...sortedLanes.map((lane, i) => ({
                id: lane.id,
                width: lane.width,
                height: lane.height,
                position: {
                  x: lane.position.x,
                  y: lane.position.y,
                },
                data: {
                  ...lane.data,
                  index: i + 1,
                },
              })),
            ],
          },
        },
      ]);
    },
    deleteLane: (laneId) => {
      const laneGroupNodes = get().swimlane.getFullSwimlaneGroup(laneId);
      const lane = get().nodesById[laneId];
      if (!laneGroupNodes) return;
      const { group, sortedLanes } = laneGroupNodes;
      if (sortedLanes.length <= 1) {
        get().removeNodes([group.id]);
        return;
      }
      const laneIndex = sortedLanes.findIndex((l) => l.id === laneId);
      if (laneIndex === -1) return;
      const lanesToShift = sortedLanes.slice(laneIndex + 1);
      const lanesBeforeShift = sortedLanes.slice(0, laneIndex);

      const deltaX = group.data.orientation === 'horizontal' ? 0 : -lane.width;
      const deltaY = group.data.orientation === 'horizontal' ? -lane.height : 0;

      const patches = shiftSwimlanes(lanesToShift, group, deltaX, deltaY);

      get().patchNodes(patches);
      get().recordOperation([
        {
          type: 'delete:swimlane',
          status: 'end',
          id: group.id,
          data: {
            before: [group, ...sortedLanes],
            after: [
              group,
              ...lanesBeforeShift,
              ...patches.map((p) => {
                const n = get().nodesById[p.id] as SwimlaneNodeModel;
                return { ...n, data: { ...n.data, index: n.data.index } };
              }),
            ],
          },
        },
      ]);
    },
  },
  meta: {
    receiveUpdate: (data) => {
      set((s) => ({
        data: {
          ...s.data,
          ...data,
        } as DiagramModel,
      }));
    },
    updateData: (data) => {
      set((s) => ({
        data: {
          ...s.data,
          ...data,
        } as DiagramModel,
      }));
      updateDiagramDataInRTDB(get().data.id, data);
      get().recordOperation([
        {
          type: 'update:diagram',
          status: 'end',
          id: 'diagram',
          data,
        },
      ]);
    },
    updateSettings: (settings) => {
      const data = {
        ...get().data,
        settings: {
          ...get().data.settings,
          ...settings,
        },
      };
      updateDiagramDataInRTDB(data.id, data);
      set(() => ({
        data,
      }));
      get().recordOperation([
        {
          type: 'update:settings',
          status: 'end',
          id: 'settings',
          data: settings,
        },
      ]);
    },
    toggleSnapToGrid: () => {
      const settings = get().data?.settings;
      if (!settings) return;
      const snapToGrid = !settings.snapToGrid;
      const edges = get().rfEdges;

      const updatedEdges = edges.map((edge) => ({
        id: edge.id,
        patch: {
          type: snapToGrid ? 'insertSmooth' : 'insert',
        } as Partial<EdgeModel>,
      }));
      const data = {
        ...get().data,
        settings: {
          ...get().data.settings,
          snapToGrid,
        },
      };

      set(() => ({
        data,
      }));

      updateDiagramDataInRTDB(data.id, data);
      get().patchEdges(updatedEdges);
      get().recordOperation([
        {
          type: 'toggle:snapToGrid',
          status: 'end',
          id: 'snapToGrid',
        },
      ]);
    },
  },
});
